"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

export const artifactsToolRegistry = {
  searchArtifacts: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./lookup-tools").then(({ SearchArtifactsToolView }) => ({
        default: SearchArtifactsToolView,
      }))
    ),
  },
  stageArtifactFiles: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./lookup-tools").then(({ StageArtifactFilesToolView }) => ({
        default: StageArtifactFilesToolView,
      }))
    ),
  },
  publishArtifact: {
    kind: "standalone",
    category: "create",
    view: lazy(() =>
      import("./publish-artifact").then(({ PublishArtifactToolView }) => ({
        default: PublishArtifactToolView,
      }))
    ),
  },
  getArtifactBindingDataSpec: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./data-tools").then(({ GetArtifactBindingDataSpecToolView }) => ({
        default: GetArtifactBindingDataSpecToolView,
      }))
    ),
  },
  setArtifactSourceData: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./data-tools").then(({ SetArtifactSourceDataToolView }) => ({
        default: SetArtifactSourceDataToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant of `artifactsToolRegistry` keyed by the
 * progressive-disclosure inner-tool name `artifacts_${toolName}`.
 *
 * Artifacts is a progressive-disclosure capability (ADR-057), so its tool
 * calls are dispatched through the `callCapabilityTool` proxy and every
 * durable + live-stream tool part is rewritten to the namespaced inner
 * identity `${capabilityId}_${toolName}` (e.g. `artifacts_publishArtifact`).
 * The bare-name entries above stay in the static tier (used by any legacy /
 * non-PD path); these namespaced entries reuse the same lazy views so the rich
 * publish card renders on the PD path instead of the generic JSON fallback.
 * Wired into the built-in registry in `built-in-registry.ts`.
 */
export const artifactsProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(artifactsToolRegistry).map(([name, meta]) => [
    `artifacts_${name}`,
    meta,
  ])
);
